/**
 * Leading at Gallo Analytics API Service
 * Handles all analytics-related API calls for the Leading at Gallo program
 */

// Env-aware: prefer REACT_APP_API_URL (append /leading), fall back to PROD gateway.
const LEADING_API_URL = process.env.REACT_APP_API_URL
  ? `${process.env.REACT_APP_API_URL.replace(/\/$/, '')}/leading`
  : 'https://nh9pshlcw7.execute-api.us-west-2.amazonaws.com/prod/leading';

/**
 * Make authenticated API request
 */
async function makeRequest(endpoint, options = {}, getAccessToken) {
  try {
    const token = await getAccessToken();
    
    const response = await fetch(`${LEADING_API_URL}${endpoint}`, {
      ...options,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...options.headers
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error(`API Error [${endpoint}]:`, error);
    throw error;
  }
}

/**
 * Leading Analytics API
 */
export const leadingAnalyticsAPI = {
  /**
   * Get all cohorts with participant counts
   */
  getCohorts: async (getAccessToken, filters = {}) => {
    let endpoint = '/api/cohorts';
    
    if (filters.programLevel && filters.programLevel !== 'all') {
      endpoint += `?program_level=${filters.programLevel}`;
    }
    
    const response = await makeRequest(endpoint, {}, getAccessToken);
    return response.data;
  },

  /**
   * Get participants for a specific cohort
   * Note: This uses the cohort endpoint which includes participants
   */
  getCohortParticipants: async (getAccessToken, cohortId) => {
    const endpoint = `/api/cohorts/${cohortId}`;
    const response = await makeRequest(endpoint, {}, getAccessToken);
    return response.data.cohort.participants || [];
  },

  /**
   * Get all participants with pagination and deduplication
   */
  getAllParticipants: async (getAccessToken, filters = {}, page = 1, limit = 10, search = '') => {
    let endpoint = `/api/analytics/participants?page=${page}&limit=${limit}`;
    
    if (search) {
      endpoint += `&search=${encodeURIComponent(search)}`;
    }
    
    if (filters.programLevel && filters.programLevel !== 'all') {
      endpoint += `&program_level=${filters.programLevel}`;
    }
    
    const response = await makeRequest(endpoint, {}, getAccessToken);
    return response.data;
  },

  /**
   * Get individual participant progress for a specific cohort
   */
  getParticipantProgress: async (getAccessToken, participantId) => {
    const endpoint = `/api/analytics/participant/${participantId}`;
    const response = await makeRequest(endpoint, {}, getAccessToken);
    return response.data;
  },

  /**
   * Get participant progress across all cohorts (multi-cohort view)
   */
  getParticipantMultiCohortProgress: async (getAccessToken, employeeId) => {
    const endpoint = `/api/analytics/participant/0?employee_id=${employeeId}`;
    const response = await makeRequest(endpoint, {}, getAccessToken);
    return response.data;
  },

  /**
   * Get all cohort participations for an employee
   */
  getEmployeeCohorts: async (getAccessToken, employeeId) => {
    const endpoint = `/api/analytics/employee/${employeeId}/cohorts`;
    const response = await makeRequest(endpoint, {}, getAccessToken);
    return response.data;
  },

  /**
   * Get cohort analytics
   */
  getCohortAnalytics: async (getAccessToken, cohortId) => {
    const endpoint = `/api/analytics/cohort/${cohortId}`;
    const response = await makeRequest(endpoint, {}, getAccessToken);
    return response.data;
  },

  /**
   * Get cohort comparison data
   */
  getCohortComparison: async (getAccessToken, cohortIds) => {
    const endpoint = `/api/analytics/comparison?cohort_ids=${cohortIds.join(',')}`;
    const response = await makeRequest(endpoint, {}, getAccessToken);
    return response.data;
  },

  /**
   * Get overall program analytics (all participants)
   */
  getOverallAnalytics: async (getAccessToken, filters = {}) => {
    let endpoint = '/api/analytics/overall';
    
    const params = new URLSearchParams();
    if (filters.programLevel && filters.programLevel !== 'all') {
      params.append('program_level', filters.programLevel);
    }
    if (filters.dateRange && filters.dateRange !== 'all') {
      params.append('date_range', filters.dateRange);
    }
    
    if (params.toString()) {
      endpoint += `?${params.toString()}`;
    }
    
    const response = await makeRequest(endpoint, {}, getAccessToken);
    return response.data;
  },

  /**
   * Export analytics data
   */
  exportData: async (getAccessToken, exportType, format, data) => {
    const endpoint = '/api/analytics/export';
    const response = await makeRequest(endpoint, {
      method: 'POST',
      body: JSON.stringify({
        export_type: exportType,
        format: format,
        ...data
      })
    }, getAccessToken);
    return response.data;
  }
};

export default leadingAnalyticsAPI;
